import { useEffect, useState } from 'react';

/** Sub-component cho 1 link (Thể lệ hoặc Chính sách) — có tab URL / Nội dung */
function LinkEditor({
    title,
    labelKey, labelVal, labelPlaceholder,
    urlKey,   urlVal,
    contentKey, contentVal, contentPlaceholder,
    onUpdate,
}: {
    title: string;
    labelKey: string;   labelVal: string;   labelPlaceholder: string;
    urlKey: string;     urlVal: string;
    contentKey: string; contentVal: string; contentPlaceholder: string;
    onUpdate: (patch: Record<string, string>) => void;
}) {
    const [tab, setTab] = useState<'url' | 'content'>(contentVal ? 'content' : 'url');

    // Sync tab khi field thay đổi (vd: switch sang checkbox field khác)
    useEffect(() => {
        setTab(contentVal ? 'content' : 'url');
    }, [contentKey]); // reset khi đổi field, không reset khi gõ
    const tabCls = (t: 'url' | 'content') =>
        `px-3 py-1.5 text-xs font-medium rounded-t border-b-2 transition-colors ${tab === t
            ? 'border-indigo-500 text-indigo-600 bg-white'
            : 'border-transparent text-gray-500 hover:text-gray-700 bg-gray-100'}`;

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <p className="text-xs font-medium text-gray-600">{title}</p>

            {/* Tên hiển thị */}
            <div>
                <label className="block text-xs text-gray-500 mb-1">Tên hiển thị</label>
                <input
                    type="text"
                    value={labelVal}
                    onChange={e => onUpdate({ [labelKey]: e.target.value })}
                    placeholder={labelPlaceholder}
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none bg-white"
                />
            </div>

            {/* Tab URL / Nhập nội dung */}
            <div>
                <div className="flex gap-1 mb-0">
                    <button type="button" className={tabCls('url')}     onClick={() => setTab('url')}>🔗 Link URL</button>
                    <button type="button" className={tabCls('content')} onClick={() => setTab('content')}>📝 Nhập nội dung</button>
                </div>
                <div className="border border-gray-200 rounded-b rounded-tr bg-white p-2">
                    {tab === 'url' ? (
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">URL trang điều khoản</label>
                            <div className="flex gap-1">
                                <select
                                    onChange={e => { if (e.target.value) onUpdate({ [urlKey]: e.target.value }); }}
                                    className="rounded border border-gray-300 px-1.5 py-1.5 text-xs text-gray-600 focus:outline-none"
                                >
                                    {URL_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                                <input
                                    type="text"
                                    value={urlVal}
                                    onChange={e => onUpdate({ [urlKey]: e.target.value })}
                                    placeholder="https://... hoặc /the-le"
                                    className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                />
                            </div>
                            {urlVal && (
                                <p className="text-xs text-gray-400 mt-1">
                                    → Khi click sẽ mở: <span className="font-mono text-blue-600">{urlVal}</span>
                                </p>
                            )}
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">
                                Nội dung điều khoản <span className="text-gray-300">(hỗ trợ xuống dòng, hiển thị trong modal)</span>
                            </label>
                            <textarea
                                value={contentVal}
                                onChange={e => onUpdate({ [contentKey]: e.target.value })}
                                rows={6}
                                placeholder={contentPlaceholder}
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none resize-y"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                → SDK sẽ hiển thị nội dung này trong popup khi người dùng click vào link.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Editor toàn bộ phần checkbox điều khoản */
function CheckboxLinkEditor({ field, idx, updateField }: {
    field: FormField;
    idx: number;
    updateField: (idx: number, patch: Partial<FormField>) => void;
}) {
    const update = (patch: Record<string, string>) => updateField(idx, { ...patch, description: buildCheckboxDescription({ ...field, ...patch }) } as Partial<FormField>);

    return (
        <div className="md:col-span-2 space-y-3">

            {/* Câu dẫn */}
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                    Câu dẫn <span className="font-normal text-gray-400">(tùy chỉnh theo từng campaign)</span>
                </label>
                <input
                    type="text"
                    value={field.intro_text || ''}
                    onChange={e => update({ intro_text: e.target.value })}
                    placeholder="Tôi đã đọc và đồng ý với"
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                />
            </div>

            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">🔗 Điều khoản & Chính sách</p>

            <LinkEditor
                title="Link 1 — Thể lệ chương trình"
                labelKey="terms_label"       labelVal={field.terms_label || ''}   labelPlaceholder="Thể lệ chương trình"
                urlKey="terms_url"           urlVal={field.terms_url || ''}
                contentKey="terms_content"   contentVal={field.terms_content || ''} contentPlaceholder={"Điều 1. Đối tượng tham gia...\nĐiều 2. Thời gian...\nĐiều 3. Giải thưởng..."}
                onUpdate={update}
            />

            <LinkEditor
                title="Link 2 — Chính sách bảo mật"
                labelKey="privacy_label"       labelVal={field.privacy_label || ''}   labelPlaceholder="Chính sách bảo mật"
                urlKey="privacy_url"           urlVal={field.privacy_url || ''}
                contentKey="privacy_content"   contentVal={field.privacy_content || ''} contentPlaceholder={"Chúng tôi cam kết bảo mật thông tin của bạn...\n\n1. Thông tin thu thập...\n2. Mục đích sử dụng..."}
                onUpdate={update}
            />

            {/* Preview */}
            <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3">
                <p className="text-xs text-indigo-400 mb-2">Xem trước:</p>
                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-not-allowed">
                    <input type="checkbox" disabled className="mt-0.5 rounded shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: buildCheckboxDescription(field) }} />
                </label>
            </div>
        </div>
    );
}

export interface FormField {
    key: string;
    label: string;
    type: 'text' | 'tel' | 'email' | 'number' | 'date' | 'select' | 'multiselect' | 'radio' | 'textarea' | 'checkbox' | 'hidden';
    required: boolean;
    isSystem?: boolean;      // field mặc định hệ thống, không xóa được
    placeholder?: string;
    options?: string[];      // for select type
    description?: string;    // for checkbox: HTML text hiển thị (auto-generated)
    intro_text?: string;     // câu dẫn tùy chỉnh
    terms_label?: string;    // tên link điều khoản
    terms_url?: string;      // URL điều khoản (nếu dùng link ngoài)
    terms_content?: string;  // nội dung điều khoản (nếu nhập trực tiếp)
    privacy_label?: string;  // tên link chính sách
    privacy_url?: string;    // URL chính sách (nếu dùng link ngoài)
    privacy_content?: string;// nội dung chính sách (nếu nhập trực tiếp)
}

/** Các URL gợi ý có sẵn — admin chọn nhanh hoặc gõ tay */
const URL_PRESETS = [
    { label: '— Nhập URL tùy chỉnh —', value: '' },
    { label: '/the-le (nội bộ)',         value: '/the-le' },
    { label: '/chinh-sach (nội bộ)',     value: '/chinh-sach' },
    { label: '/dieu-khoan (nội bộ)',     value: '/dieu-khoan' },
    { label: '/privacy (nội bộ)',        value: '/privacy' },
];

/** Build HTML description từ các fields có cấu trúc */
function buildCheckboxDescription(field: FormField): string {
    const intro  = field.intro_text   || 'Tôi đã đọc và đồng ý với';
    const tLabel = field.terms_label  || 'Thể lệ chương trình';
    const pLabel = field.privacy_label|| 'Chính sách bảo mật';
    const tUrl   = field.terms_url    || '#';
    const pUrl   = field.privacy_url  || '#';

    const hasTerms   = field.terms_label   || field.terms_url;
    const hasPrivacy = field.privacy_label || field.privacy_url;

    const link = (href: string, text: string) =>
        `<a href="${href}" target="_blank" rel="noopener" class="text-blue-600 underline">${text}</a>`;

    if (hasTerms && hasPrivacy) {
        return `${intro} ${link(tUrl, tLabel)} và ${link(pUrl, pLabel)}.`;
    } else if (hasTerms) {
        return `${intro} ${link(tUrl, tLabel)}.`;
    } else if (hasPrivacy) {
        return `${intro} ${link(pUrl, pLabel)}.`;
    }
    return field.description || `${intro} các điều khoản.`;
}

const FIELD_TYPES = [
    { value: 'text',        label: 'Văn bản ngắn' },
    { value: 'tel',         label: 'Số điện thoại' },
    { value: 'email',       label: 'Email' },
    { value: 'number',      label: 'Số' },
    { value: 'date',        label: '📅 Ngày tháng' },
    { value: 'textarea',    label: 'Văn bản dài' },
    { value: 'select',      label: 'Danh sách (chọn 1)' },
    { value: 'multiselect', label: 'Danh sách (chọn nhiều)' },
    { value: 'radio',       label: '🔘 Radio (chọn 1)' },
    { value: 'checkbox',    label: '☑ Điều khoản / Xác nhận' },
    { value: 'hidden',      label: '👻 Ẩn (tự điền từ URL)' },
];

/** Field mặc định hệ thống — luôn có, không xóa được */
export const SYSTEM_FIELDS: FormField[] = [
    { key: 'name',  label: 'Họ và tên',     type: 'text', required: true,  placeholder: 'Nguyễn Văn A',    isSystem: true },
    { key: 'phone', label: 'Số điện thoại', type: 'tel',  required: true,  placeholder: '0912 345 678', isSystem: true },
];

const PRESET_FIELDS: FormField[] = [
    { key: 'email',         label: 'Email',              type: 'email',    required: false, placeholder: 'email@example.com' },
    { key: 'province',      label: 'Tỉnh/Thành phố',     type: 'select',   required: false, options: [
        'Hà Nội','TP. Hồ Chí Minh','Đà Nẵng','Hải Phòng','Cần Thơ',
        'An Giang','Bà Rịa - Vũng Tàu','Bắc Giang','Bắc Kạn','Bạc Liêu',
        'Bắc Ninh','Bến Tre','Bình Định','Bình Dương','Bình Phước',
        'Bình Thuận','Cà Mau','Cao Bằng','Đắk Lắk','Đắk Nông',
        'Điện Biên','Đồng Nai','Đồng Tháp','Gia Lai','Hà Giang',
        'Hà Nam','Hà Tĩnh','Hải Dương','Hậu Giang','Hòa Bình',
        'Hưng Yên','Khánh Hòa','Kiên Giang','Kon Tum','Lai Châu',
        'Lâm Đồng','Lạng Sơn','Lào Cai','Long An','Nam Định',
        'Nghệ An','Ninh Bình','Ninh Thuận','Phú Thọ','Phú Yên',
        'Quảng Bình','Quảng Nam','Quảng Ngãi','Quảng Ninh','Quảng Trị',
        'Sóc Trăng','Sơn La','Tây Ninh','Thái Bình','Thái Nguyên',
        'Thanh Hóa','Thừa Thiên Huế','Tiền Giang','Trà Vinh','Tuyên Quang',
        'Vĩnh Long','Vĩnh Phúc','Yên Bái',
    ]},
    { key: 'address',       label: 'Địa chỉ',            type: 'text',     required: false, placeholder: 'Số nhà, đường, phường/xã...' },
    { key: 'dob',           label: 'Ngày sinh',          type: 'date',     required: false, placeholder: '' },
    { key: 'gender',        label: 'Giới tính',          type: 'radio',    required: false, options: ['Nam', 'Nữ', 'Khác'] },
    { key: 'cccd',          label: 'CCCD/CMND',          type: 'text',     required: false, placeholder: 'Nhập số CCCD (9-12 chữ số)' },
    { key: 'referral_code', label: 'Mã giới thiệu',      type: 'text',     required: false, placeholder: 'Nhập mã giới thiệu (nếu có)' },
    { key: 'utm_source',    label: 'Nguồn (ẩn)',         type: 'hidden',   required: false, placeholder: 'utm_source' },
    {
        key: 'agree_terms',
        label: 'Đồng ý điều khoản',
        type: 'checkbox',
        required: true,
        terms_label:   'Thể lệ chương trình',
        terms_url:     '',
        privacy_label: 'Chính sách bảo mật',
        privacy_url:   '',
    },
];

function generateKey(label: string): string {
    return label
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

export default function FormFieldsEditor({
    fields,
    onChange,
    themeId = 'blue',
}: {
    fields: FormField[];
    onChange: (fields: FormField[]) => void;
    themeId?: string;
}) {
    const [editingIdx, setEditingIdx] = useState<number | null>(null);

    // Đảm bảo 2 field hệ thống luôn tồn tại ở đầu danh sách
    useEffect(() => {
        const missing = SYSTEM_FIELDS.filter(sf => !fields.some(f => f.key === sf.key));
        if (missing.length > 0) {
            onChange([...missing, ...fields]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addPreset = (preset: FormField) => {
        if (fields.some(f => f.key === preset.key)) return; // prevent duplicate
        onChange([...fields, { ...preset }]);
    };

    const addCustom = () => {
        const newField: FormField = {
            key: `custom_${Date.now()}`,
            label: 'Trường mới',
            type: 'text',
            required: false,
            placeholder: '',
        };
        onChange([...fields, newField]);
        setEditingIdx(fields.length);
    };

    const updateField = (idx: number, patch: Partial<FormField>) => {
        const updated = fields.map((f, i) => {
            if (i !== idx) return f;
            const merged = { ...f, ...patch };
            // Auto-generate key from label if key is auto-generated
            if (patch.label && merged.key.startsWith('custom_')) {
                merged.key = generateKey(patch.label) || merged.key;
            }
            return merged;
        });
        onChange(updated);
    };

    const removeField = (idx: number) => {
        if (fields[idx]?.isSystem) return; // khóa xóa field hệ thống
        onChange(fields.filter((_, i) => i !== idx));
        if (editingIdx === idx) setEditingIdx(null);
    };

    const moveField = (idx: number, dir: -1 | 1) => {
        const newFields = [...fields];
        const target = idx + dir;
        if (target < 0 || target >= newFields.length) return;
        [newFields[idx], newFields[target]] = [newFields[target], newFields[idx]];
        onChange(newFields);
    };

    const editingField = editingIdx !== null ? fields[editingIdx] : null;

    return (
        <div className="space-y-4">
            {/* Quick add presets */}
            <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Thêm nhanh trường có sẵn:</p>
                <div className="flex flex-wrap gap-2">
                    {PRESET_FIELDS.map(preset => {
                        const used = fields.some(f => f.key === preset.key);
                        return (
                            <button
                                key={preset.key}
                                type="button"
                                disabled={used}
                                onClick={() => addPreset(preset)}
                                className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${used
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer'
                                    }`}
                            >
                                {used ? '✓' : '+'} {preset.label}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        onClick={addCustom}
                        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-gray-400 hover:bg-gray-50"
                    >
                        + Trường tùy chỉnh
                    </button>
                </div>
            </div>

            {/* Fields list */}
            {fields.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
                    Chưa có trường nào — thêm từ danh sách trên
                </div>
            ) : (
                <div className="space-y-2">
                    {fields.map((field, idx) => (
                        <div key={field.key} className={`rounded-lg border bg-white ${
                            field.isSystem ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'
                        }`}>
                            {/* Field row */}
                            <div className="flex items-center gap-3 px-3 py-2.5">
                                <div className="flex flex-col gap-0.5">
                                    <button type="button" onClick={() => moveField(idx, -1)} disabled={idx === 0}
                                        className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs">▲</button>
                                    <button type="button" onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1}
                                        className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs">▼</button>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {field.isSystem && (
                                            <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700 font-semibold border border-amber-200">
                                                🔒 Mặc định
                                            </span>
                                        )}
                                        <span className="font-medium text-sm text-gray-900">{field.label}</span>
                                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 font-mono">{field.key}</span>
                                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">{FIELD_TYPES.find(t => t.value === field.type)?.label}</span>
                                        {field.required && <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-500 font-medium">Bắt buộc</span>}
                                    </div>
                                    {field.placeholder && (
                                        <div className="text-xs text-gray-400 truncate mt-0.5">Placeholder: {field.placeholder}</div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                                    className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                                >
                                    {editingIdx === idx ? '▲ Thu gọn' : '✏️ Sửa'}
                                </button>
                                {!field.isSystem && (
                                    <button
                                        type="button"
                                        onClick={() => removeField(idx)}
                                        className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                                    >
                                        🗑
                                    </button>
                                )}
                            </div>

                            {/* Inline edit panel */}
                            {editingIdx === idx && editingField && (
                                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Nhãn hiển thị *</label>
                                        <input
                                            type="text"
                                            value={editingField.label}
                                            onChange={e => updateField(idx, { label: e.target.value })}
                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Key (tên biến)</label>
                                        <input
                                            type="text"
                                            value={editingField.key}
                                            disabled={!!editingField.isSystem}
                                            onChange={e => updateField(idx, { key: e.target.value.replace(/\s/g, '_') })}
                                            className={`w-full rounded border px-2 py-1.5 text-sm font-mono focus:border-indigo-500 focus:outline-none ${
                                                editingField.isSystem ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-300'
                                            }`}
                                        />
                                        {editingField.isSystem && (
                                            <p className="text-xs text-amber-600 mt-0.5">🔒 Khóa — field hệ thống</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Loại trường</label>
                                        <select
                                            value={editingField.type}
                                            onChange={e => updateField(idx, { type: e.target.value as FormField['type'] })}
                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                        >
                                            {FIELD_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
                                        <input
                                            type="text"
                                            value={editingField.placeholder || ''}
                                            onChange={e => updateField(idx, { placeholder: e.target.value })}
                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                        />
                                    </div>
                                    {(editingField.type === 'select' || editingField.type === 'multiselect' || editingField.type === 'radio') && (
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Các lựa chọn <span className="font-normal text-gray-400">(mỗi dòng một lựa chọn)</span>
                                            </label>
                                            <textarea
                                                value={(editingField.options || []).join('\n')}
                                                onChange={e => updateField(idx, { options: e.target.value.split('\n').filter(Boolean) })}
                                                rows={4}
                                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">
                                                {editingField.type === 'radio' ? '🔘 Hiển thị dạng nút radio, người dùng chọn 1' :
                                                 editingField.type === 'multiselect' ? '☑ Người dùng có thể chọn nhiều' :
                                                 '▼ Dropdown chọn 1 lựa chọn'}
                                            </p>
                                        </div>
                                    )}
                                    {editingField.type === 'hidden' && (
                                        <div className="md:col-span-2 rounded-lg bg-gray-50 border border-dashed border-gray-300 px-4 py-3">
                                            <p className="text-xs font-medium text-gray-600 mb-1">👻 Trường ẩn — tự điền từ URL parameter</p>
                                            <p className="text-xs text-gray-400 mb-2">Khi nhúng game, thêm <code className="bg-gray-200 px-1 rounded">?{editingField.placeholder || editingField.key}=giá_trị</code> vào URL để tự điền trường này.</p>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Tên URL param</label>
                                                <input
                                                    type="text"
                                                    value={editingField.placeholder || ''}
                                                    onChange={e => updateField(idx, { placeholder: e.target.value })}
                                                    placeholder="utm_source, ref, partner..."
                                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm font-mono focus:border-indigo-500 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {editingField.type === 'date' && (
                                        <div className="md:col-span-2 rounded-lg bg-blue-50 border border-blue-100 px-4 py-2">
                                            <p className="text-xs text-blue-600">📅 Hiển thị dạng date picker — người dùng chọn ngày tháng năm</p>
                                        </div>
                                    )}
                                     {editingField.type === 'checkbox' && (<CheckboxLinkEditor field={editingField as any} idx={idx} updateField={updateField} /> )}
                                    {((editingField.type as string) === 'checkbox_DISABLED') && (
                                        <div className="md:col-span-2 space-y-3">

                                            {/* Câu dẫn tùy chỉnh */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Câu dẫn <span className="font-normal text-gray-400">(tùy chỉnh theo từng campaign)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editingField.intro_text || ''}
                                                    onChange={e => updateField(idx, {
                                                        intro_text: e.target.value,
                                                        description: buildCheckboxDescription({ ...editingField, intro_text: e.target.value }),
                                                    })}
                                                    placeholder="Tôi đã đọc và đồng ý với"
                                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                                />
                                            </div>

                                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">🔗 Liên kết điều khoản</p>

                                            {/* Link 1 — Thể lệ */}
                                            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                                                <p className="text-xs font-medium text-gray-500">Link 1 — Thể lệ chương trình</p>
                                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Tên hiển thị</label>
                                                        <input
                                                            type="text"
                                                            value={editingField.terms_label || ''}
                                                            onChange={e => updateField(idx, {
                                                                terms_label: e.target.value,
                                                                description: buildCheckboxDescription({ ...editingField, terms_label: e.target.value }),
                                                            })}
                                                            placeholder="Thể lệ chương trình"
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">URL <span className="text-gray-400">(gõ hoặc chọn nhanh)</span></label>
                                                        <div className="flex gap-1">
                                                            <select
                                                                onChange={e => { if (e.target.value) updateField(idx, { terms_url: e.target.value, description: buildCheckboxDescription({ ...editingField, terms_url: e.target.value }) }); }}
                                                                className="rounded border border-gray-300 px-1.5 py-1.5 text-xs text-gray-600 focus:border-indigo-500 focus:outline-none"
                                                            >
                                                                {URL_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                                            </select>
                                                            <input
                                                                type="text"
                                                                value={editingField.terms_url || ''}
                                                                onChange={e => updateField(idx, {
                                                                    terms_url: e.target.value,
                                                                    description: buildCheckboxDescription({ ...editingField, terms_url: e.target.value }),
                                                                })}
                                                                placeholder="https://... hoặc /the-le"
                                                                className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Link 2 — Chính sách */}
                                            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                                                <p className="text-xs font-medium text-gray-500">Link 2 — Chính sách bảo mật</p>
                                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Tên hiển thị</label>
                                                        <input
                                                            type="text"
                                                            value={editingField.privacy_label || ''}
                                                            onChange={e => updateField(idx, {
                                                                privacy_label: e.target.value,
                                                                description: buildCheckboxDescription({ ...editingField, privacy_label: e.target.value }),
                                                            })}
                                                            placeholder="Chính sách bảo mật"
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">URL <span className="text-gray-400">(gõ hoặc chọn nhanh)</span></label>
                                                        <div className="flex gap-1">
                                                            <select
                                                                onChange={e => { if (e.target.value) updateField(idx, { privacy_url: e.target.value, description: buildCheckboxDescription({ ...editingField, privacy_url: e.target.value }) }); }}
                                                                className="rounded border border-gray-300 px-1.5 py-1.5 text-xs text-gray-600 focus:border-indigo-500 focus:outline-none"
                                                            >
                                                                {URL_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                                            </select>
                                                            <input
                                                                type="text"
                                                                value={editingField.privacy_url || ''}
                                                                onChange={e => updateField(idx, {
                                                                    privacy_url: e.target.value,
                                                                    description: buildCheckboxDescription({ ...editingField, privacy_url: e.target.value }),
                                                                })}
                                                                placeholder="https://... hoặc /chinh-sach"
                                                                className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Live preview */}
                                            <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3">
                                                <p className="text-xs text-indigo-400 mb-2">Xem trước:</p>
                                                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-not-allowed">
                                                    <input type="checkbox" disabled className="mt-0.5 rounded shrink-0" />
                                                    <span dangerouslySetInnerHTML={{ __html: buildCheckboxDescription(editingField) }} />
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div className="md:col-span-2 flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id={`req-${idx}`}
                                            checked={editingField.required}
                                            onChange={e => updateField(idx, { required: e.target.checked })}
                                            className="rounded"
                                        />
                                        <label htmlFor={`req-${idx}`} className="text-sm text-gray-700">Trường bắt buộc</label>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Themed Preview */}
            {fields.length > 0 && (() => {
                const FORM_THEMES: Record<string, {
                    cardBg: string;
                    cardBorder: string;
                    title: string;
                    inputFocus: string;
                    submitBg: string;
                    submitColor: string;
                    overlayBg: string;
                }> = {
                    red:       { cardBg: 'linear-gradient(160deg,#1a0505,#350a0a)', cardBorder: 'rgba(255,215,0,.4)',   title: '#ffd700', inputFocus: 'rgba(255,215,0,.6)',   submitBg: 'linear-gradient(135deg,#ffd700,#daa520)', submitColor: '#1a0800', overlayBg: 'rgba(20,5,5,0.96)' },
                    blue:      { cardBg: 'linear-gradient(160deg,#020617,#0c1445)', cardBorder: 'rgba(0,240,255,.4)',   title: '#22d3ee', inputFocus: 'rgba(0,240,255,.6)',   submitBg: 'linear-gradient(135deg,#0ea5e9,#0284c7)', submitColor: '#ffffff', overlayBg: 'rgba(2,6,23,0.96)' },
                    gold:      { cardBg: 'linear-gradient(160deg,#1a1000,#2d1f00)', cardBorder: 'rgba(255,215,0,.45)',  title: '#ffd700', inputFocus: 'rgba(255,215,0,.65)',  submitBg: 'linear-gradient(135deg,#ffd700,#daa520)', submitColor: '#1a0800', overlayBg: 'rgba(26,16,0,0.96)' },
                    green:     { cardBg: 'linear-gradient(160deg,#021207,#04230f)', cardBorder: 'rgba(74,222,128,.4)',  title: '#86efac', inputFocus: 'rgba(74,222,128,.65)', submitBg: 'linear-gradient(135deg,#22c55e,#16a34a)', submitColor: '#ffffff', overlayBg: 'rgba(2,12,7,0.96)' },
                    cyberpunk: { cardBg: 'linear-gradient(160deg,#050015,#0d002a)', cardBorder: 'rgba(0,255,255,.5)',   title: '#00ffff', inputFocus: 'rgba(0,255,255,.7)',   submitBg: 'linear-gradient(135deg,#00ffff,#0080ff)', submitColor: '#050015', overlayBg: 'rgba(5,0,21,0.96)' },
                    sunset:    { cardBg: 'linear-gradient(160deg,#1e0f07,#3b1e0f)', cardBorder: 'rgba(249,115,22,.4)',   title: '#fdba74', inputFocus: 'rgba(249,115,22,.6)',   submitBg: 'linear-gradient(135deg,#f97316,#ea580c)', submitColor: '#ffffff', overlayBg: 'rgba(20,10,5,0.96)' },
                    violet:    { cardBg: 'linear-gradient(160deg,#0d061e,#1c0e3a)', cardBorder: 'rgba(139,92,246,.5)',   title: '#c084fc', inputFocus: 'rgba(139,92,246,.7)',   submitBg: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', submitColor: '#ffffff', overlayBg: 'rgba(10,5,20,0.96)' },
                };
                const theme = FORM_THEMES[themeId] || FORM_THEMES['blue'];
                const themeName = themeId === 'red' ? 'Đỏ Lễ Hội'
                                : themeId === 'gold' ? 'Vàng Hoàng Kim'
                                : themeId === 'green' ? 'Xanh Sinh Thái'
                                : themeId === 'cyberpunk' || themeId === 'neon' ? 'Cyberpunk Neon'
                                : themeId === 'sunset' ? 'Sunset Glow'
                                : themeId === 'violet' ? 'Midnight Violet'
                                : 'Xanh Công Nghệ';

                const inputStyle = {
                    background: 'rgba(255,255,255,.07)',
                    border: '1.5px solid rgba(255,255,255,.15)',
                    color: '#fff',
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box' as const,
                };

                return (
                    <div className="rounded-2xl p-6 shadow-xl border themed-preview-container"
                        style={{
                            background: theme.cardBg,
                            borderColor: theme.cardBorder,
                        }}
                    >
                        <style>{`
                            .themed-preview-container a {
                                color: ${theme.title} !important;
                                text-decoration: underline !important;
                                font-weight: 600;
                            }
                            .themed-preview-container input::placeholder,
                            .themed-preview-container textarea::placeholder {
                                color: rgba(255,255,255,.25) !important;
                            }
                        `}</style>
                        <p className="text-xs font-bold uppercase tracking-wider mb-1 text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>
                            Xem trước biểu mẫu
                        </p>
                        <h4 className="text-base font-extrabold text-center mb-4" style={{ color: theme.title }}>
                            CHỦ ĐỀ: {themeName}
                        </h4>
                        <div className="space-y-4 max-w-sm mx-auto">
                            {fields.map(field => (
                                <div key={field.key}>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.65)' }}>
                                        {field.label} {field.required && <span className="text-rose-500 font-bold">*</span>}
                                    </label>
                                    {field.type === 'hidden' ? (
                                        <div style={{ ...inputStyle, opacity: 0.4, fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>👻 Trường ẩn — tự điền từ URL: ?{field.placeholder || field.key}=...</div>
                                    ) : field.type === 'select' ? (
                                        <select disabled style={inputStyle} className="opacity-90">
                                            <option>-- Chọn {field.label} --</option>
                                            {(field.options || []).map(o => <option key={o}>{o}</option>)}
                                        </select>
                                    ) : field.type === 'multiselect' ? (
                                        <div style={{ ...inputStyle, padding: '6px 12px' }} className="opacity-90 space-y-1">
                                            {(field.options || ['Lựa chọn 1', 'Lựa chọn 2']).slice(0, 3).map(o => (
                                                <label key={o} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                                    <input type="checkbox" disabled className="rounded accent-emerald-500" />
                                                    {o}
                                                </label>
                                            ))}
                                        </div>
                                    ) : field.type === 'radio' ? (
                                        <div style={{ ...inputStyle, padding: '6px 12px' }} className="opacity-90 space-y-1">
                                            {(field.options || ['Lựa chọn 1', 'Lựa chọn 2']).map(o => (
                                                <label key={o} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                                    <input type="radio" disabled name={`preview-${field.key}`} className="accent-emerald-500" />
                                                    {o}
                                                </label>
                                            ))}
                                        </div>
                                    ) : field.type === 'date' ? (
                                        <input disabled type="date" style={inputStyle} className="opacity-90" />
                                    ) : field.type === 'textarea' ? (
                                        <textarea disabled placeholder={field.placeholder} rows={2} style={inputStyle} className="resize-none opacity-90" />
                                    ) : field.type === 'checkbox' ? (
                                        <label className="flex items-start gap-2.5 text-xs cursor-not-allowed select-none" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                            <input type="checkbox" disabled className="mt-0.5 rounded border-gray-600 bg-gray-800 accent-emerald-500" />
                                            {field.description
                                                ? <span dangerouslySetInnerHTML={{ __html: field.description }} />
                                                : <span>{field.label}</span>
                                            }
                                        </label>
                                    ) : (
                                        <input disabled type={field.type === 'tel' ? 'tel' : 'text'} placeholder={field.placeholder} style={inputStyle} className="opacity-90" />
                                    )}
                                </div>
                            ))}
                            <button disabled className="w-full mt-4 py-2.5 px-4 rounded-xl font-bold text-sm tracking-wide transition-all shadow-md"
                                style={{
                                    background: theme.submitBg,
                                    color: theme.submitColor,
                                    opacity: 0.9,
                                }}
                            >
                                GỬI THÔNG TIN
                            </button>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
